/**
 * SNAP-2424: recovery and cancel tokens must not reach Sentry in URLs, breadcrumbs or transaction
 * names. This is a minimal scrubber for those routes only. The sec-07 scrubber (frontend PR 124)
 * covers more and will overlap with it.
 */
const RECOVERY_TOKEN_IN_PATH = /(\/account-recovery\/(?:cancel\/)?)([^/?#\s"'&]+)/g;
const REDACTED = 'redacted';

export const redactRecoveryTokens = (value: string): string =>
  value.replace(RECOVERY_TOKEN_IN_PATH, (match: string, prefix: string, token: string) =>
    token === REDACTED ? match : `${prefix}${REDACTED}`
  );

const isPlainObject = (value: object): boolean => {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const redactDeep = (value: unknown, seen = new WeakMap<object, unknown>()): unknown => {
  if (typeof value === 'string') return redactRecoveryTokens(value);
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return seen.get(value);

  if (Array.isArray(value)) {
    const copy: unknown[] = [];
    seen.set(value, copy);
    value.forEach((item) => copy.push(redactDeep(item, seen)));
    return copy;
  }
  if (!isPlainObject(value)) return value;

  const source = value as Record<string, unknown>;
  const copy: Record<string, unknown> = {};
  seen.set(value, copy);
  Object.keys(source).forEach((key) => {
    copy[key] = redactDeep(source[key], seen);
  });
  return copy;
};

export const scrubSentryEvent = <T>(event: T): T => redactDeep(event) as T;

export const scrubSentryBreadcrumb = <T>(breadcrumb: T): T => redactDeep(breadcrumb) as T;
